import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Typography,
  Avatar,
  FormHelperText,
} from '@mui/material';
import {
  Business,
  LocationOn,
  Phone,
  Email,
} from '@mui/icons-material';
import { Fornecedor } from '../../types/fornecedor';
import { FornecedorService } from '../../services/fornecedorService';

interface FornecedorSelectProps {
  value?: number | string | null;
  onChange: (fornecedorId: number | null, fornecedor?: Fornecedor) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  allowClear?: boolean;
}

const FornecedorSelect: React.FC<FornecedorSelectProps> = ({
  value = null,
  onChange,
  label = 'Fornecedor',
  placeholder = 'Digite para buscar um fornecedor',
  required = false,
  disabled = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  variant = 'outlined',
  allowClear = true,
}) => {
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedFornecedor, setSelectedFornecedor] = useState<Fornecedor | null>(null);

  // Buscar fornecedores baseado no input
  const searchFornecedores = async (searchTerm: string = '') => {
    try {
      setLoading(true);
      setLoadError(null);

      const filters: any = {
        limit: 50, // Limitar resultados para performance
      };

      // Se há termo de busca, adicionar aos filtros
      if (searchTerm.trim()) {
        filters.search = searchTerm.trim();
      }

      const response = await FornecedorService.buscarFornecedoresComFiltros(filters);

      if (response.success && response.data) {
        setFornecedores(response.data);
      } else {
        setLoadError('Erro ao carregar fornecedores');
        setFornecedores([]);
      }
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
      setLoadError('Erro ao buscar fornecedores');
      setFornecedores([]);
    } finally {
      setLoading(false);
    }
  };

  // Carregar fornecedores iniciais
  useEffect(() => {
    searchFornecedores();
  }, []);

  // Buscar quando o input muda (com debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue.length >= 2 || inputValue.length === 0) {
        searchFornecedores(inputValue);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  // Encontrar fornecedor selecionado baseado no value
  useEffect(() => {
    if (value) {
      const fornecedorId = typeof value === 'string' ? parseInt(value) : value;
      const fornecedor = fornecedores.find(f => f.id === fornecedorId);
      setSelectedFornecedor(fornecedor || null);
    } else {
      setSelectedFornecedor(null);
    }
  }, [value, fornecedores]);

  const handleChange = (_: any, newValue: Fornecedor | null) => {
    setSelectedFornecedor(newValue);
    onChange(newValue?.id || null, newValue || undefined);
  };

  const formatFornecedorOption = (fornecedor: Fornecedor) => {
    return `${fornecedor.nome} - ${fornecedor.cidade}/${fornecedor.estado}`;
  };

  const getStatusColor = (status?: string) => {
    // Como não há status definido no tipo Fornecedor, usamos uma cor padrão
    return 'primary';
  };

  const getStatusLabel = (status?: string) => {
    // Como não há status definido no tipo Fornecedor, retornamos um label padrão
    return 'Ativo';
  };

  return (
    <FormControl 
      fullWidth={fullWidth} 
      error={error}
      disabled={disabled}
      size={size}
    >
      {loadError && (
        <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem' }}>
          {loadError}
        </Alert>
      )}
      
      <Autocomplete
        value={selectedFornecedor}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        options={fornecedores}
        getOptionLabel={(option) => formatFornecedorOption(option)}
        loading={loading}
        disabled={disabled}
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        freeSolo={false}
        disableClearable={!allowClear}
        filterOptions={(x) => x} // Não filtrar aqui, já filtramos no backend
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label + (required ? ' *' : '')}
            placeholder={placeholder}
            variant={variant}
            size={size}
            error={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                <Business />
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                  {option.nome}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                  {option.cnpj && `${option.cnpj} • `}{option.cidade}/{option.estado}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                <Chip
                  size="small"
                  label={getStatusLabel()}
                  color={getStatusColor() as any}
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.65rem' }}
                />
                {option.vendedor && (
                  <Chip
                    size="small"
                    label={option.vendedor}
                    variant="outlined"
                    sx={{ height: 16, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 2 
            ? "Digite pelo menos 2 caracteres para buscar"
            : loading 
              ? "Buscando..."
              : "Nenhum fornecedor encontrado"
        }
      />
      
      {helperText && (
        <FormHelperText sx={{ fontSize: '0.75rem' }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FornecedorSelect;
